import { useState } from "react";
import { NavLink, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { useNotifications } from "@/context/NotificationsContext";
import { useI18n } from "@/context/I18nContext";
import LanguageSwitcher from "@/components/LanguageSwitcher";
import { Logo } from "@/components/Logo";
import {
  LayoutDashboard, Egg, Bird, Package, ShoppingCart, Users, Wallet, Truck, Milk, Baby,
  Store, BarChart3, Bell, LifeBuoy, Settings, CreditCard, Menu, LogOut,
  Home, Warehouse, Plus, X, TrendingUp, Building2, HeartPulse, Star, Info, Upload, FileText, ExternalLink,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Sheet, SheetContent, SheetTrigger, SheetTitle } from "@/components/ui/sheet";
import { Badge } from "@/components/ui/badge";
import { speciesByKey, housingLabel } from "@/i18n/species";
import { hr } from "@/i18n/health_referral";
import { bl } from "@/i18n/billing_i18n";
import { rv } from "@/i18n/reviews_i18n";
import { navImport } from "@/i18n/import_i18n";
import UpgradeBanner from "@/components/UpgradeBanner";
import QuotaAlertPill from "@/components/QuotaAlertPill";

const POULTRY_KEYS = ["poules", "canards", "oies", "dindes"];

const nav = [
  { to: "/app", k: "home", icon: LayoutDashboard, end: true },
  { to: "/app/elevage", k: "elevage", icon: Bird },
  { to: "/app/sante", k: "health", icon: HeartPulse },
  { to: "/app/oeufs", k: "eggs", icon: Egg, need: "eggs" },
  { to: "/app/production", k: "production", icon: Milk },
  { to: "/app/reproduction", k: "reproduction", icon: Baby },
  { to: "/app/poulaillers", k: "houses", icon: Home, need: "housing" },
  { to: "/app/produits", k: "products", icon: Package },
  { to: "/app/stock", k: "stock", icon: Warehouse },
  { to: "/app/commandes", k: "orders", icon: ShoppingCart },
  { to: "/app/clients", k: "customers", icon: Users },
  { to: "/app/factures", k: "invoices", icon: FileText },
  { to: "/app/fournisseurs", k: "suppliers", icon: Building2 },
  { to: "/app/depenses", k: "expenses", icon: Wallet },
  { to: "/app/comptabilite", k: "accounting", icon: TrendingUp },
  { to: "/app/livraisons", k: "deliveries", icon: Truck },
  { to: "/app/boutique", k: "shop", icon: Store },
  { to: "/app/avis", k: "reviews", icon: Star },
  { to: "/app/statistiques", k: "stats", icon: BarChart3 },
  { to: "/app/abonnement", k: "subscription", icon: CreditCard },
  { to: "/app/support", k: "support", icon: LifeBuoy },
  { to: "/app/parametres", k: "settings", icon: Settings },
  { to: "/app/import", k: "import", icon: Upload },
];

const mobileNav = [
  { to: "/app", k: "home", icon: Home, end: true },
  { to: "/app/elevage", k: "elevage", icon: Bird },
  { to: "/app/oeufs", k: "eggs", icon: Egg, need: "eggs" },
  { to: "/app/stock", k: "stock", icon: Warehouse },
  { to: "/app/commandes", k: "orders", icon: ShoppingCart },
];

function speciesGate(species) {
  const list = species || [];
  const hasEggs = list.some((k) => speciesByKey(k)?.prod === "eggs");
  const hasPoultry = list.some((k) => POULTRY_KEYS.includes(k));
  return (n) => {
    if (!n.need) return true;
    if (n.need === "eggs") return hasEggs;
    if (n.need === "poultry") return hasPoultry;
    if (n.need === "housing") return list.length > 0;
    return true;
  };
}

function SidebarLinks({ onClick, d, items, housesLabel, lang }) {
  return (
    <nav className="flex flex-col gap-1 px-3 pb-6">
      {items.map((n) => (
        <NavLink
          key={n.to}
          to={n.to}
          end={n.end}
          onClick={onClick}
          data-testid={`nav-${n.k}`}
          className={({ isActive }) =>
            `flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm font-medium transition-colors ${
              isActive ? "bg-primary text-primary-foreground shadow-sm" : "text-muted-foreground hover:bg-muted"
            }`
          }
        >
          <n.icon className="h-[18px] w-[18px]" />
          {n.k === "houses" ? housesLabel : n.k === "health" ? hr(lang, "title") : n.k === "reviews" ? rv(lang, "navReviews") : n.k === "import" ? navImport(lang) : n.k === "invoices" ? bl(lang, "title") : d.navItems[n.k]}
        </NavLink>
      ))}
    </nav>
  );
}

export default function Layout({ children, titleKey }) {
  const { user, farm, logout } = useAuth();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const { unread } = useNotifications();
  const { d, lang } = useI18n();
  const gate = speciesGate(farm?.species);
  const navItems = nav.filter(gate);
  const mNav = mobileNav.filter(gate);
  const hasEggs = (farm?.species || []).some((k) => speciesByKey(k)?.prod === "eggs");
  const housesLabel = housingLabel(lang, farm?.species);
  const title = titleKey === "houses" ? housesLabel : titleKey === "health" ? hr(lang, "title") : titleKey === "reviews" ? rv(lang, "navReviews") : titleKey === "invoices" ? bl(lang, "title") : (titleKey ? d.navItems[titleKey] : "");

  return (
    <div className="min-h-screen bg-background">
      {/* Desktop sidebar */}
      <aside className="hidden lg:flex fixed inset-y-0 left-0 w-64 flex-col border-r border-border bg-card z-30">
        <div className="px-4 py-5 flex flex-col items-start gap-1.5">
          <NavLink to="/app" data-testid="dashboard-logo"><Logo className="h-16 w-auto" /></NavLink>
          <p className="text-xs text-muted-foreground truncate max-w-[220px]">{farm?.name}</p>
        </div>
        <div className="flex-1 overflow-y-auto">
          <SidebarLinks d={d} items={navItems} housesLabel={housesLabel} lang={lang} />
        </div>
        <div className="p-3 border-t border-border space-y-1">
          <NavLink
            to="/"
            data-testid="view-site-link"
            className="flex w-full items-center gap-3 px-3 py-2.5 rounded-xl text-sm font-medium text-muted-foreground hover:bg-muted"
          >
            <ExternalLink className="h-[18px] w-[18px]" /> {d.navItems.viewSite}
          </NavLink>
          <button
            onClick={logout}
            data-testid="logout-button"
            className="flex w-full items-center gap-3 px-3 py-2.5 rounded-xl text-sm font-medium text-muted-foreground hover:bg-muted"
          >
            <LogOut className="h-[18px] w-[18px]" /> {d.navItems.logout}
          </button>
        </div>
      </aside>

      {/* Main */}
      <div className="lg:pl-64">
        {/* Topbar */}
        <header className="sticky top-0 z-20 bg-background/80 backdrop-blur-xl border-b border-border">
          <div className="flex items-center justify-between px-4 lg:px-8 h-16">
            <div className="flex items-center gap-3">
              <Sheet open={open} onOpenChange={setOpen}>
                <SheetTrigger asChild>
                  <Button variant="ghost" size="icon" className="lg:hidden" data-testid="mobile-menu-button">
                    <Menu className="h-5 w-5" />
                  </Button>
                </SheetTrigger>
                <SheetContent side="left" className="p-0 w-72 flex flex-col">
                  <SheetTitle className="sr-only">Menu</SheetTitle>
                  <div className="px-4 py-5 shrink-0">
                    <NavLink to="/app" onClick={() => setOpen(false)}><Logo className="h-14 w-auto" /></NavLink>
                  </div>
                  <div className="flex-1 overflow-y-auto min-h-0">
                    <SidebarLinks onClick={() => setOpen(false)} d={d} items={navItems} housesLabel={housesLabel} lang={lang} />
                  </div>
                  <div className="p-3 border-t border-border space-y-1 shrink-0">
                    <NavLink to="/" onClick={() => setOpen(false)} data-testid="view-site-link-mobile" className="flex w-full items-center gap-3 px-3 py-2.5 rounded-xl text-sm text-muted-foreground hover:bg-muted">
                      <ExternalLink className="h-[18px] w-[18px]" /> {d.navItems.viewSite}
                    </NavLink>
                    <button onClick={logout} data-testid="logout-button-mobile" className="flex w-full items-center gap-3 px-3 py-2.5 rounded-xl text-sm text-muted-foreground hover:bg-muted">
                      <LogOut className="h-[18px] w-[18px]" /> {d.navItems.logout}
                    </button>
                  </div>
                </SheetContent>
              </Sheet>
              <h1 className="font-head font-extrabold text-lg">{title}</h1>
            </div>
            <div className="flex items-center gap-2">
              <NavLink to="/forum" className="hidden sm:inline text-sm font-medium text-[#2C4234] hover:text-[#D96C4A] transition-colors" data-testid="dash-forum-link">Forum</NavLink>
              <QuotaAlertPill />
              <LanguageSwitcher />
              <button
                onClick={() => navigate("/app/notifications")}
                data-testid="notifications-button"
                className="relative h-10 w-10 grid place-items-center rounded-xl hover:bg-muted"
              >
                <Bell className="h-5 w-5" />
                {unread > 0 && (
                  <Badge className="absolute -top-1 -right-1 h-5 min-w-5 px-1 bg-primary text-[10px]">{unread}</Badge>
                )}
              </button>
              <div className="h-9 w-9 rounded-full bg-secondary text-secondary-foreground grid place-items-center text-sm font-bold">
                {(user?.first_name?.[0] || "") + (user?.last_name?.[0] || "")}
              </div>
            </div>
          </div>
        </header>

        <main className="px-4 lg:px-8 py-6 pb-28 lg:pb-10 max-w-[1400px]">
          {farm?.slug === "la-ferme-des-poules-heureuses" && (
            <div className="mb-4 flex flex-wrap items-center justify-between gap-3 rounded-xl border border-[#D96C4A]/30 bg-[#D96C4A]/10 px-4 py-2.5 text-sm" data-testid="demo-banner">
              <span className="flex items-center gap-2 font-semibold text-[#BF583A]"><Info className="h-4 w-4 shrink-0" /> {rv(lang, "demoBanner")}</span>
              <NavLink to="/register"><Button size="sm" className="rounded-full bg-[#D96C4A] hover:bg-[#BF583A] h-8" data-testid="demo-banner-cta">{rv(lang, "demoBannerCta")}</Button></NavLink>
            </div>
          )}
          <UpgradeBanner />
          {children}
        </main>
      </div>

      {/* Mobile bottom nav */}
      <nav className="lg:hidden fixed bottom-0 inset-x-0 z-30 bg-card/95 backdrop-blur-md border-t border-border">
        <div className="grid h-16" style={{ gridTemplateColumns: `repeat(${mNav.length}, minmax(0, 1fr))` }}>
          {mNav.map((n) => (
            <NavLink
              key={n.to}
              to={n.to}
              end={n.end}
              data-testid={`mobilenav-${n.k}`}
              className={({ isActive }) =>
                `flex flex-col items-center justify-center gap-0.5 text-[11px] font-medium ${
                  isActive ? "text-primary" : "text-muted-foreground"
                }`
              }
            >
              <n.icon className="h-5 w-5" />
              {d.navItems[n.k]}
            </NavLink>
          ))}
        </div>
      </nav>

      {/* FAB quick record production */}
      {hasEggs && (
        <button
          onClick={() => navigate("/app/oeufs?nouvelle=1")}
          data-testid="fab-record-production"
          className="fixed bottom-20 right-4 lg:bottom-8 lg:right-8 z-40 h-14 px-5 rounded-full bg-accent text-accent-foreground font-bold shadow-lg flex items-center gap-2 hover:scale-105 active:scale-95 transition-transform"
        >
          <Plus className="h-5 w-5" /> <span className="hidden sm:inline">{d.navItems.quickRecord}</span>
          <span className="sm:hidden">{d.navItems.eggs}</span>
        </button>
      )}
    </div>
  );
}
